import { formatPhoneNumberWithExtension } from '../stringification/phone';
import { GlossarySettingsSchema } from './glossary';

// Global prompt parameters use %{{PARAM_NAME}} syntax
// Team, settings and skill placeholders use ${{path}} syntax
//
// NOTE(wassim): backslashes are accepted so a markdown-escaped placeholder is
// still found — a markdown editor emits `FOO\_BAR` for `FOO_BAR` on every save.
export const GLOBAL_PROMPT_PLACEHOLDER_REGEX = /%\{\{([\w\\]+)\}\}/g;
export const SETTINGS_PLACEHOLDER_REGEX = /\$\{\{([a-zA-Z0-9_.\\]+)\}\}/g;

export const stripPlaceholderEscapes = (path: string): string =>
  path.replace(/\\/g, '');

export const extractInstructionPlaceholderPaths = (text: string): string[] => {
  const paths = new Set<string>();
  const regex = new RegExp(SETTINGS_PLACEHOLDER_REGEX.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    paths.add(stripPlaceholderEscapes(match[1]));
  }

  return Array.from(paths);
};

// NOTE(wassim): only the inside of a placeholder is unescaped; prompt bodies
// carry legitimate backslashes elsewhere, such as `\:\:` casts in SQL examples.
export const normalizePlaceholderEscapes = (text: string): string =>
  text
    .replace(
      new RegExp(SETTINGS_PLACEHOLDER_REGEX.source, 'g'),
      (_match, path: string) => `\${{${stripPlaceholderEscapes(path)}}}`
    )
    .replace(
      new RegExp(GLOBAL_PROMPT_PLACEHOLDER_REGEX.source, 'g'),
      (_match, name: string) => `%{{${stripPlaceholderEscapes(name)}}}`
    );

export type InstructionPlaceholder<T> = {
  path: string;
  description: string;
  formatter?: (value: T) => string;
};

export type PhoneNumber = {
  phoneNumber?: string;
  extension?: string;
};

const INSTRUCTION_PLACEHOLDERS: InstructionPlaceholder<string>[] = [
  {
    path: 'team.name',
    description: 'The name of the team',
  },
  {
    path: 'team.id',
    description: 'The Vooma UUID of the team',
  },
  {
    path: 'team.email',
    description:
      'The email address of the team, plus the team after-hours CC address while the after-hours schedule (or manual override) is active',
  },
];

export const UNSET_INSTRUCTION_PLACEHOLDER_VALUE = '(not set)';

export const ORGANIZATION_INSTRUCTION_PLACEHOLDERS: InstructionPlaceholder<string>[] =
  [
    {
      path: 'organization.name',
      description:
        'The organization operating name for agent identity, falling back to the organization name. Prefer organization.operatingName for new prompts.',
    },
    {
      path: 'organization.operatingName',
      description:
        'The organization operating name for agent identity (carrier-facing), falling back to the organization name',
    },
    {
      path: 'organization.pronounceableName',
      description:
        'The pronounceable operating name for voice agents, falling back to the operating name then organization name. Prefer organization.pronounceableOperatingName for new prompts.',
    },
    {
      path: 'organization.pronounceableOperatingName',
      description:
        'The pronounceable operating name for voice agents, falling back to the operating name then organization name',
    },
    {
      path: 'organization.defaultTimezone',
      description:
        'The organization default IANA timezone, or (not set) when unknown',
    },
    {
      path: 'organization.mcNumber',
      description: "The organization's MC number, or (not set) when unknown",
    },
  ];

export const WORKER_INSTRUCTION_PLACEHOLDERS: InstructionPlaceholder<string>[] =
  [
    {
      path: 'worker.emailAddress',
      description:
        'The executing worker mailbox address from a connected Gmail, Outlook, or AgentMail token, or (not set) when none is connected',
    },
  ];

const PHONE_PLACEHOLDER: InstructionPlaceholder<
  PhoneNumber | null | undefined
>[] = [
  {
    path: 'team.phoneNumber',
    description:
      'The phone number of the team with extension (formatted as +1 (XXX) XXX-XXXX xXXX)',
    formatter: (phoneNumber?: PhoneNumber | null) => {
      if (!phoneNumber?.phoneNumber) return '';
      return formatPhoneNumberWithExtension(
        phoneNumber.phoneNumber,
        phoneNumber.extension
      );
    },
  },
];

export const PROCEDURE_INSTRUCTION_PLACEHOLDERS = [
  ...INSTRUCTION_PLACEHOLDERS,
  ...PHONE_PLACEHOLDER,
];

const GLOSSARY_PLACEHOLDER_PATHS = Object.keys(
  GlossarySettingsSchema.shape
).map((key) => `glossary.${key}`);

export const VOOMA_SETTINGS_PLACEHOLDER_PATHS = new Set([
  'cover.negotiationGuidance',
  ...GLOSSARY_PLACEHOLDER_PATHS,
]);
